import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import {
  ArrowRight,
  TrendingUp,
  Wrench,
  Users,
  Package,
  BookOpen,
  HeadphonesIcon,
  Brain,
  Smartphone,
  Zap,
  FolderKanban,
  Link2,
  Play,
} from "lucide-react";
import GalleryCarousel from "../components/ScreenshotCarousel";
import PageMeta from "../components/PageMeta";
import { DEMO_URL } from "../lib/urls";

const MODULES = [
  { slug: "sales-flow", name: "Rogue SalesFlow", tagline: "Suspects, leads, quotes, invoices. AI lead intel built in.", icon: TrendingUp },
  { slug: "ops", name: "Rogue Ops", tagline: "Job cards, scheduling, GPS, photos, signatures. Field service done right.", icon: Wrench },
  { slug: "teams", name: "Rogue Teams", tagline: "Time & attendance, tasks, chat, recruitment, roles — your entire workforce.", icon: Users },
  { slug: "projects", name: "Rogue Projects", tagline: "Gantt timelines, budgets, risk registers, profitability — full project management.", icon: FolderKanban },
  { slug: "stockroom", name: "Rogue Stockroom", tagline: "Inventory, suppliers, OCR price-lists, procurement, datasheets.", icon: Package },
  { slug: "bookkeep", name: "Rogue Bookkeep", tagline: "Expenses, invoices, statements, profitability — bookkeeping that explains itself.", icon: BookOpen },
  { slug: "support", name: "Rogue Support", tagline: "Tickets, SLAs, and Cerebrum drafts every reply.", icon: HeadphonesIcon },
  { slug: "connect", name: "Rogue Connect", tagline: "Branded customer portal — self-service tickets, orders, documents, communication.", icon: Link2 },
];

const WHY_ROGUE = [
  { title: "Built for operators everywhere", desc: "Multi-currency, multi-language, multi-country. Designed for how real teams actually work.", icon: Zap },
  { title: "Mobile-first", desc: "Your field team runs Rogue from their phone. Every screen, every workflow — mobile-native.", icon: Smartphone },
  { title: "Cerebrum AI available", desc: "AI isn't a separate product. Add Cerebrum to any module — it drafts, detects, researches, and coaches across your whole operation.", icon: Brain },
];

export default function RogueHub({ onTrialClick }) {
  const { symbol, rate } = useCurrency();
  const cx = (usd) => Math.round(usd * rate);
  return (
    <div className="min-h-screen">
      <PageMeta title="Rogue" description="Run the operation. One platform, eight modules — CRM, field service, teams, inventory, bookkeeping, support, recruitment, and AI. Buy only what you need." path="/space/rogue" />
      {/* Hero */}
      <section data-testid="rogue-hero" className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(/images/hero-rogue.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-geminii-deep/30 via-geminii-deep/60 to-geminii-deep" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mb-6"
          >
            <img src="/logos/rogue/white-blue.png" alt="Geminii Rogue" className="h-10 sm:h-12 w-auto" data-testid="rogue-hero-logo" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.08] tracking-tight max-w-3xl"
          >
            Run the operation.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            className="mt-2 font-display text-xl text-geminii-muted"
          >
            Powered by Geminii Space.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
            className="mt-6 text-lg text-geminii-muted max-w-2xl leading-relaxed"
          >
            One platform. Eight modules. Buy only what you need.
          </motion.p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={onTrialClick}
              data-testid="rogue-hero-cta"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
            >
              Start free trial — no credit card
              <ArrowRight size={16} />
            </button>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rogue-hero-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-rogue-cyan/30 text-rogue-cyan font-semibold text-sm hover:bg-rogue-cyan/10 hover:border-rogue-cyan/60 transition-all duration-250"
            >
              <Play size={14} className="fill-rogue-cyan" />
              View live demo
            </a>
            <Link
              to="/space/rogue/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>

      {/* App Preview */}
      <section data-testid="rogue-preview" className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-4">See it in action</h2>
          <p className="text-geminii-muted mb-10 max-w-lg">Real screenshots from a live Rogue deployment — dark mode, light mode, and mobile.</p>
          <GalleryCarousel
            screenshots={[
              { src: "/screenshots/dark-dashboard.png", label: "Dashboard overview — dark mode", mode: "dark" },
              { src: "/screenshots/light-dashboard.png", label: "Dashboard overview — light mode", mode: "light" },
              { src: "/screenshots/dark-leads.png", label: "Lead pipeline", mode: "dark" },
              { src: "/screenshots/light-leads.png", label: "Lead pipeline — light mode", mode: "light" },
              { src: "/screenshots/dark-analytics.png", label: "Analytics & reporting", mode: "dark" },
              { src: "/screenshots/light-analytics.png", label: "Analytics — light mode", mode: "light" },
              { src: "/screenshots/dark-timeline.png", label: "Timeline / Gantt", mode: "dark" },
              { src: "/screenshots/light-timeline.png", label: "Timeline — light mode", mode: "light" },
              { src: "/screenshots/dark-jobcards.png", label: "Field service job cards", mode: "dark" },
              { src: "/screenshots/dark-salesfunnel.png", label: "Sales funnel", mode: "dark" },
              { src: "/screenshots/dark-projects.png", label: "Project management", mode: "dark" },
              { src: "/screenshots/dark-scheduler.png", label: "Team scheduling", mode: "dark" },
              { src: "/screenshots/dark-tickets.png", label: "Support tickets", mode: "dark" },
              { src: "/screenshots/mobile-dashboard.png", label: "Dashboard — mobile", mode: "mobile" },
              { src: "/screenshots/mobile-leads.png", label: "Leads — mobile", mode: "mobile" },
              { src: "/screenshots/mobile-jobcards.png", label: "Job cards — mobile", mode: "mobile" },
            ]}
          />
        </div>
      </section>

      {/* Module Grid */}
      <section data-testid="rogue-modules" className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-4">Eight modules. Your pick.</h2>
          <p className="text-geminii-muted mb-12 max-w-lg">
            Each module works on its own or together. Cerebrum AI connects them all.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MODULES.map((mod, i) => (
              <motion.div
                key={mod.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                whileHover={{ scale: 1.02 }}
              >
                <Link
                  to={`/space/rogue/${mod.slug}`}
                  data-testid={`rogue-module-${mod.slug}`}
                  className="block h-full bg-geminii-card rounded-xl border border-white/[0.06] hover:border-rogue-cyan/30 p-6 transition-all duration-250 hover:shadow-[0_0_24px_-4px_rgba(31,182,193,0.2)] group"
                >
                  <mod.icon size={22} className="text-rogue-cyan mb-3" />
                  <h3 className="font-display font-bold text-sm text-white mb-2">{mod.name}</h3>
                  <p className="text-xs text-geminii-muted leading-relaxed">{mod.tagline}</p>
                  <div className="mt-4 flex items-center gap-1 text-rogue-cyan text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRight size={12} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Cerebrum Add-on */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8"
          >
            <Link
              to="/space/rogue/cerebrum"
              data-testid="rogue-module-cerebrum"
              className="block bg-gradient-to-r from-cerebrum-violet/10 to-transparent border border-cerebrum-violet/20 hover:border-cerebrum-violet/40 rounded-xl p-6 transition-all duration-250 group"
            >
              <div className="flex items-center gap-4">
                <Brain size={24} className="text-cerebrum-violet" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display font-bold text-sm text-white">Cerebrum AI</h3>
                    <span className="text-[10px] font-mono text-cerebrum-violet bg-cerebrum-violet/10 px-2 py-0.5 rounded">Add-on</span>
                  </div>
                  <p className="text-xs text-geminii-muted">The AI layer that lives inside every module. Research, draft, detect, coach — add it to any plan.</p>
                </div>
                <div className="text-cerebrum-violet text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  Learn more <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>
      <section data-testid="rogue-why" className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-14">Why Rogue</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {WHY_ROGUE.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <item.icon size={28} className="text-rogue-cyan mb-4" />
                <h3 className="font-display font-semibold text-base text-white mb-2">{item.title}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Teaser */}
      <section data-testid="rogue-case-study" className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link
              to="/space/rogue/case-study"
              data-testid="rogue-case-study-link"
              className="block bg-gradient-to-br from-geminii-card to-geminii-deep border border-rogue-cyan/15 rounded-2xl p-8 md:p-12 hover:border-rogue-cyan/30 transition-all duration-300 group"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-mono text-rogue-cyan bg-rogue-cyan/10 px-2 py-1 rounded">Case Study</span>
              </div>
              <h3 className="font-display font-bold text-2xl text-white mb-3 group-hover:text-rogue-cyan transition-colors">
                See how a real business runs everything on Rogue
              </h3>
              <p className="text-geminii-muted max-w-xl mb-6 leading-relaxed">
                From suspect tracking to field service, from project management to analytics — an interactive walkthrough of Rogue powering an entire operation end to end.
              </p>
              <span className="inline-flex items-center gap-2 text-rogue-cyan text-sm font-medium">
                Explore the case study
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-12 md:py-16 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-4">
            Get the full platform — or pick your modules.
          </h2>
          <p className="text-geminii-muted mb-2 max-w-md mx-auto">
            All Rogue from <span className="line-through text-geminii-dim">{symbol} {cx(49).toLocaleString()}/mo</span>{" "}
            <span className="text-rogue-cyan font-semibold">{symbol} {cx(25).toLocaleString()}/mo</span> for the first 3 months.
          </p>
          <p className="text-xs text-geminii-dim mb-8">5 users, 8 modules, no credit card to start.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/space/rogue/pricing"
              data-testid="rogue-pricing-link"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rogue-cyan text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(31,182,193,0.35)] transition-all duration-250"
            >
              View pricing
              <ArrowRight size={16} />
            </Link>
            <button
              onClick={onTrialClick}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-rogue-cyan/50 hover:text-rogue-cyan transition-all duration-250"
            >
              Start free trial
            </button>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rogue-closing-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-rogue-cyan/30 text-rogue-cyan font-semibold text-sm hover:bg-rogue-cyan/10 hover:border-rogue-cyan/60 transition-all duration-250"
            >
              <Play size={14} className="fill-rogue-cyan" />
              View live demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
